import request from '@/utils/request';

/**
 * 创建商品
 * @param {Object} params - 商品信息
 * @param {string} params.marketID - 商店ID
 * @param {string} params.name - 商品名称
 * @param {string} params.categoryID - 分类ID (可选)
 * @param {string} params.organizationID - 组织ID (可选)
 * @param {string} params.tags - 标签,逗号分隔 (可选)
 * @param {boolean} params.recommended - 是否推荐置顶 (可选)
 * @param {string} params.logo - 商品Logo URL (可选)
 * @param {string} params.introduction - 商品简介 (可选)
 * @param {string} params.screenshots - 产品截图 (可选)
 * @param {string} params.installDoc - 安装使用文档 (可选)
 * @param {string} params.description - 商品介绍 (可选)
 * @param {string} params.userID - 创建用户ID
 * @param {string} params.status - 上下架状态: online/offline
 */
export function createProduct(params) {
  const { marketID, ...data } = params;
  return request({
    url: `/app-server/markets/${marketID}/products`,
    method: 'post',
    data
  });
}

/**
 * 更新商品
 * @param {Object} params - 商品信息
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 */
export function updateProduct(params) {
  const { marketID, productID, ...data } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}`,
    method: 'put',
    data
  });
}

/**
 * 删除商品
 * @param {Object} params - 参数
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 */
export function deleteProduct(params) {
  const { marketID, productID } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}`,
    method: 'delete'
  });
}

/**
 * 获取商品详情
 * @param {Object} params - 参数
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 */
export function getProductDetail(params) {
  const { marketID, productID } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}`,
    method: 'get'
  });
}

/**
 * 获取商品列表
 * @param {Object} params - 查询参数
 * @param {string} params.marketID - 商店ID
 * @param {string} params.query - 搜索关键词 (可选)
 * @param {string} params.categoryID - 分类ID筛选 (可选)
 * @param {string} params.organizationID - 组织ID筛选 (可选)
 * @param {string} params.status - 状态筛选: online/offline (可选)
 * @param {number} params.page - 页码 (可选,默认1)
 * @param {number} params.pageSize - 每页大小 (可选,默认10)
 */
export function getProductList(params) {
  const { marketID, ...queryParams } = params;
  return request({
    url: `/app-server/markets/${marketID}/products`,
    method: 'get',
    params: queryParams
  });
}

/**
 * 更改商品上下架状态
 * @param {Object} params - 参数
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 * @param {string} params.status - 状态: online/offline
 */
export function changeProductStatus(params) {
  const { marketID, productID, status } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}`,
    method: 'put',
    data: { status }
  });
}

/**
 * 创建商品套餐
 * @param {Object} params - 套餐信息
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 * @param {string} params.name - 套餐名称
 * @param {string} params.appKeyID - 应用模板ID
 * @param {string} params.version - 版本 (可选)
 * @param {number} params.price - 价格
 * @param {string} params.billtype - 计费方式 (可选)
 * @param {string} params.billCycle - 计费周期 (可选)
 * @param {string} params.packageType - 套餐类型: free/pay/business
 * @param {string} params.templateType - 模板类型 (可选)
 * @param {boolean} params.enable - 是否启用
 * @param {Array} params.items - 套餐指标 (可选)
 * @param {string} params.description - 描述 (可选)
 * @param {boolean} params.isTrial - 是否试用 (可选)
 * @param {boolean} params.enableReview - 是否需要审核 (可选)
 * @param {string} params.deliveryRegionID - 交付环境ID (可选)
 */
export function createProductPackage(params) {
  const { marketID, productID, ...data } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}/packages`,
    method: 'post',
    data
  });
}

/**
 * 更新商品套餐
 * @param {Object} params - 套餐信息
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 * @param {string} params.packageID - 套餐ID
 */
export function updateProductPackage(params) {
  const { marketID, productID, packageID, ...data } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}/packages/${packageID}`,
    method: 'put',
    data
  });
}

/**
 * 删除商品套餐
 * @param {Object} params - 参数
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 * @param {string} params.packageID - 套餐ID
 */
export function deleteProductPackage(params) {
  const { marketID, productID, packageID } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}/packages/${packageID}`,
    method: 'delete'
  });
}

/**
 * 获取商品套餐列表
 * @param {Object} params - 参数
 * @param {string} params.marketID - 商店ID
 * @param {string} params.productID - 商品ID
 */
export function getProductPackages(params) {
  const { marketID, productID } = params;
  return request({
    url: `/app-server/markets/${marketID}/products/${productID}/packages`,
    method: 'get'
  });
}

// 获取商品套餐环境变量模板（管理端）
export function fetchProductPackageEnvTemplate(params) {
  return request({
    url: `/app-server/markets/${params.marketID}/products/${params.productID}/packages/${params.packageID}/env/template`,
    method: 'get'
  });
}

// 更新商品套餐环境变量配置（管理端）
export function updateProductPackageEnvConfig(params, data) {
  return request({
    url: `/app-server/markets/${params.marketID}/products/${params.productID}/packages/${params.packageID}/env/config`,
    method: 'put',
    data
  });
}



// WEBPACK FOOTER //
// ./src/api/product.js